// ============================================================
// saveScheduleChange — スケジュール変更の保存 + メモ永続化
//
// 2026-07-03 Phase 3: 従来、移動/入替/リサイズの変更ログ memo は
// 「detail を送らない」方針の副作用で永続化されなかった（楽観更新のみで
// 次の同期で消える。Pusher 無効 + 30秒ポーリングの宮古インスタンスでは確実に消える）。
//
// 本関数は ref-save-reservation.md L76-77 の解法（fresh-fetch 方式）を実装:
//   1. サーバーから最新の予約（完全な detail 込み）を取得
//   2. サーバー側の既存メモに新エントリを追記した完全 detail を構築
//   3. __completeDetail フラグ付きで保存（handleSave と同じ battle-tested パターン）
//
// フォールバック（スケジュール変更の信頼性は従来と同等以上を保証）:
//   - fresh 取得失敗 → detail なしの schedule-only 保存（= 従来挙動、メモは非永続）
//   - メモ付き保存が失敗 → schedule-only 保存でリトライ
//   - どちらも sparse detail は絶対に送らない（regression テストで固定）
// ============================================================

import { Reservation } from './types';
import { toScheduleOnlyPayload } from './schedulePayload';
import { appendMemoEntry } from './memoLog';
import { saveDispatchReservation, fetchDispatchReservations } from '../../api/dispatchService';

/**
 * @param updated   スケジュール変更を適用済みの予約（保存対象の値）
 * @param memoEntry 永続化する変更ログエントリ（buildMemoEntry の戻り値）。
 *                  省略時は従来どおり schedule-only 保存。
 *                  ※ updated.memo（キャッシュ由来）は使わない — detail 未ロードの
 *                    キャッシュだと既存メモを含まず、サーバーのメモを消すため。
 */
export async function saveScheduleChange(updated: Reservation, memoEntry?: string): Promise<any> {
  const scheduleOnly = toScheduleOnlyPayload(updated);

  if (memoEntry) {
    let payloadWithMemo: any = null;
    try {
      const [fresh] = await fetchDispatchReservations({ id: updated.id });
      if (fresh) {
        const serverMemo = (fresh.memo ?? fresh.detail?.memo ?? '') as string;
        const mergedMemo = appendMemoEntry(serverMemo, memoEntry);
        payloadWithMemo = {
          ...fresh,                 // 完全な実値を土台に（handleSave と同じ方針B）
          ...scheduleOnly,          // スケジュール変更を上書き
          // ⚠️ トップレベル memo も必ず更新すること（2026-08-01 修正）。
          //   buildDetailPayload の pick() は `input.memo ?? input.detail.memo` の順で、
          //   **トップレベルが優先**される。`...fresh` にはサーバの古い memo が
          //   トップレベルで入っているため、detail.memo だけ更新すると追記が
          //   黙って捨てられる（保存自体は成功するのでエラーにも出ない）。
          memo: mergedMemo,
          detail: { ...(fresh.detail || {}), memo: mergedMemo },
          __completeDetail: true,   // 完全な detail として送る（sparse 500 回避）
        };
      }
    } catch (e) {
      console.warn('[saveScheduleChange] 最新detail取得に失敗 — メモなし(schedule-only)で保存します:', e);
    }
    if (payloadWithMemo) {
      try {
        return await saveDispatchReservation(payloadWithMemo);
      } catch (e) {
        console.warn('[saveScheduleChange] メモ付き保存に失敗 — schedule-only にフォールバックします:', e);
      }
    }
  }

  return saveDispatchReservation(scheduleOnly);
}

/**
 * 予約メモにエントリを追記するだけの保存（スケジュールは変更しない）。
 * サーバーの最新状態を土台にするため、編集パネルの未保存変更を巻き込まない。
 * メモ記録は主目的の操作（メール送信等）を失敗させないよう、失敗時は null を返すのみ。
 */
export async function appendReservationMemo(
  reservationId: string | number,
  memoEntry: string,
): Promise<any | null> {
  try {
    const [fresh] = await fetchDispatchReservations({ id: reservationId });
    if (!fresh) return null;
    const serverMemo = (fresh.memo ?? fresh.detail?.memo ?? '') as string;
    // ⚠️ トップレベル memo も必ず更新すること（2026-08-01 修正）。理由は
    //   saveScheduleChange 内の同じコメントを参照。detail.memo だけ更新していたため、
    //   既存メモを持つ予約では追記が黙って捨てられていた。
    const mergedMemo = appendMemoEntry(serverMemo, memoEntry);
    return await saveDispatchReservation({
      ...fresh,
      memo: mergedMemo,
      detail: { ...(fresh.detail || {}), memo: mergedMemo },
      __completeDetail: true,
    });
  } catch (e) {
    console.warn('[appendReservationMemo] メモ記録に失敗しました（本体処理は継続）:', e);
    return null;
  }
}
